import Box from '@mui/material/Box';
import { DataGrid as MuiDataGrid } from '@mui/x-data-grid';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import useBreakpoints from '../../helpers/useBreakpoints';
import { defineRowClassName, handleSortModelChange } from './dataGridHelpers';
import DefaultNoRowsOverlay from './DefaultNoRowsOverlay';
import HeaderWithEllipsisTooltip from './HeaderWithEllipsisTooltip';
import messages from './messages';
import Pagination from './Pagination';

const defaultMUIDataGridCellHeight = 52;

const styles = {
  root: {
    '& .clickableRow': {
      cursor: 'pointer',
    },
    '& .disabledRow': {
      color: 'text.disabled',
    },
    backgroundColor: 'common.white',
    width: '100%',
  },
};

const DataGrid = ({
  columns,
  components = {},
  componentsProps = {},
  customToolbar = null,
  dataTestId,
  defaultRowsNumber = 7,
  getRowClassName = undefined,
  hideFooterPagination,
  loading,
  noRowsOverlay = null,
  onOrderBy = () => {},
  onRowsPerPageChange = () => {},
  onSelectPage = () => {},
  order = '',
  page = 1,
  resultsFiltered = 0,
  resultsTotal = 0,
  rows,
  rowsPerPage = 10,
  slotProps = {},
  slots = {},
  ...props
}) => {
  const { downSm } = useBreakpoints();

  const columnsWithHeaderTooltip = useMemo(
    () =>
      columns.map((column) =>
        column.headerName && !column.renderHeader
          ? {
              ...column,
              renderHeader: (params) => <HeaderWithEllipsisTooltip headerName={params.colDef.headerName} />,
            }
          : column
      ),
    [columns]
  );

  const sortingFieldName = useMemo(() => {
    if (order !== null && order[0] === '-') {
      return order.substring(1, order.length);
    }
    return order;
  }, [order]);

  const [sortModel, setSortModel] = useState([
    { field: sortingFieldName, sort: order !== null && order[0] === '-' ? 'desc' : 'asc' },
  ]);

  const defaultRows = [...Array(defaultRowsNumber)].map((el, i) => ({
    id: i,
  }));
  const dataGridSlotProps = { ...componentsProps, ...slotProps };
  const dataGridSlots = { ...components, ...slots };

  return (
    <Box sx={styles.root} data-testid={dataTestId}>
      <MuiDataGrid
        {...props}
        getRowHeight={!loading ? () => 'auto' : undefined}
        autoHeight
        columns={columnsWithHeaderTooltip}
        getRowClassName={(params) => defineRowClassName(params, getRowClassName)}
        hideFooterPagination={hideFooterPagination || resultsFiltered === 0}
        loading={loading}
        localeText={{
          footerRowSelected: (count) => (
            <FormattedMessage
              {...messages.DataGridFooterRows}
              values={{
                count,
              }}
            />
          ),
        }}
        onSortModelChange={(newModel) => handleSortModelChange(onOrderBy, setSortModel, sortModel, newModel)}
        rows={loading && rows.length === 0 ? defaultRows : rows}
        slotProps={{
          ...dataGridSlotProps,
          loadingOverlay: {
            noRowsVariant: 'skeleton',
            variant: 'skeleton',
            ...dataGridSlotProps.loadingOverlay,
          },
          pagination: {
            colSpan: columns.length,
            labelRowsPerPage: (
              <FormattedMessage
                {...(downSm ? messages.DataGridMobilePaginationLabel : messages.DataGridDesktopPaginationLabel)}
              />
            ),
            onRowsPerPageChange,
            onSelectPage,
            page,
            resultsFiltered,
            resultsTotal,
            rowsPerPage,
          },
        }}
        slots={{
          ...dataGridSlots,
          noRowsOverlay: noRowsOverlay ? () => noRowsOverlay : DefaultNoRowsOverlay,
          pagination: Pagination,
          toolbar: customToolbar,
        }}
        sortingMode="server"
        sortModel={sortModel}
        sx={{
          '& .MuiDataGrid-cell': {
            alignItems: 'center',
            display: 'flex',
          },
          '& .MuiDataGrid-row': {
            maxHeight: loading ? 'fit-content !important' : 'none',
            minHeight: loading ? 'auto' : `${defaultMUIDataGridCellHeight}px !important`,
          },
          ...(!rows?.length &&
            !loading && {
              '& .MuiDataGrid-overlayWrapper': {
                height: 'auto',
                position: 'relative',
                top: 0,
              },
              '& .MuiDataGrid-overlayWrapperInner': {
                height: 'auto !important',
              },
              '--DataGrid-overlayHeight': 'auto',
            }),
        }}
      />
    </Box>
  );
};

DataGrid.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  components: PropTypes.shape({}),
  componentsProps: PropTypes.shape({}),
  customToolbar: PropTypes.node,
  dataTestId: PropTypes.string,
  defaultRowsNumber: PropTypes.number,
  getRowClassName: PropTypes.func,
  hideFooterPagination: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  noRowsOverlay: PropTypes.node,
  onOrderBy: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
  onSelectPage: PropTypes.func,
  order: PropTypes.string,
  page: PropTypes.number,
  resultsFiltered: PropTypes.number,
  resultsTotal: PropTypes.number,
  rows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  rowsPerPage: PropTypes.number,
  slotProps: PropTypes.shape({}),
  slots: PropTypes.shape({}),
};

export default DataGrid;
